import * as RadixContextMenu from "@radix-ui/react-context-menu";
import { Handler, useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { action, autorun, computed, IComputedValue, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, { forwardRef, useContext, useEffect, useRef } from "react";
import { uiStateMobx } from "../SharedIframeState";
import { ActionType, Pane, PaneViewMode, Position } from "../Types";
import {
  RadixContextMenuContent,
  RadixContextMenuItem,
} from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { assert, getParentByFilter } from "../Utils";
import { getPanesTopLeft, normalizePanesForClipboard } from "./CanvasClipboard";
import { canvasDispatch } from "./CanvasDispatch";
import {
  activePaneStates,
  canCreateNewPaneComputed,
  canvasAtom,
  canvasLayoutIdAtom,
  canvasTransformComputed,
  canvasViewport,
  focusedPaneIdAtom,
  isOnCustomLayoutComputed,
  isViewingLayoutComputed,
  selectedPaneIdsAtom,
} from "./CanvasState";
import { DragClipboardPasteStartEventEmitter } from "./ClipboardPane";
import { PaneComponentContext } from "./PaneComponentContext";
import { PaneNameInput, PaneTransformButtonClassName } from "./PaneHeader";
import { paneMutator } from "./PaneMutator";
import { MAGIC_LAYOUT_ID } from "./SandboxConstants";
import {
  addPositions,
  addUndoDeletionToStack,
  arePanesPartOfFunctionPane,
  convertViewportPositionToCanvasOffset,
  getPaneIdFromPaneInput,
  movePaneToTopIfNeeded,
  roundPosition,
  roundPositionToGrid,
} from "./SandboxUtils";

export function handleAltDragPane(
  selectedPanes: Pane[],
  [clientX, clientY]: Position
) {
  const clipboardPanes = normalizePanesForClipboard(selectedPanes, true);
  const mousePosition = roundPositionToGrid(
    convertViewportPositionToCanvasOffset(
      [clientX, clientY],
      canvasTransformComputed.get(),
      canvasViewport.zoom
    ),
    1
  );
  const selectedPanesTopLeft = getPanesTopLeft(selectedPanes);
  const positionOffset = addPositions(
    selectedPanesTopLeft,
    mousePosition.map((v) => -v) as Position
  );
  DragClipboardPasteStartEventEmitter.emit("mousedown", {
    clipboardSelection: {
      id: -2,
      panes: clipboardPanes.map((p) => ({
        ...p,
        rect: [
          p.rect[0] + positionOffset[0],
          p.rect[1] + positionOffset[1],
          p.rect[2],
          p.rect[3],
        ],
      })),
    },
    clientPosition: [clientX, clientY],
    isDuplicate: true,
  });
}

function getHeaderCircleButtonClassName(isFocused: boolean) {
  return classNames(
    "flex shrink-0 items-center justify-center h-3 w-3 mr-1 rounded-md bg-neutral-200 bg-opacity-50 group-hover:bg-opacity-100 text-neutral-400 focus-visible:text-neutral-0 hover:text-neutral-0 transition",
    isFocused
      ? "text-opacity-100"
      : "text-opacity-70 group-hover:text-opacity-100"
  );
}

function getUpstreamPanes(panes: Pane[], startingPanes: Pane[]): Pane[] {
  const rv: Pane[] = [...startingPanes];
  const panesToProcess = [...startingPanes];
  while (panesToProcess.length > 0) {
    let p = panesToProcess.shift()!;
    if ("inputs" in p) {
      p.inputs.forEach((paneInput) => {
        if (paneInput.source === undefined) {
          return;
        }
        const inputPane = panes.find((p) => p.id === paneInput.source![0])!;
        if (!rv.includes(inputPane)) {
          rv.push(inputPane);
          panesToProcess.push(inputPane);
        }
      });
    }
    // TODO: handle alternate inputs like template and LLM inputSource
  }
  return rv;
}

function getDownstreamPanes(panes: Pane[], startingPanes: Pane[]): Pane[] {
  const rv: Pane[] = [...startingPanes];
  const panesToProcess = [...startingPanes];
  while (panesToProcess.length > 0) {
    let paneToProcess = panesToProcess.shift()!;
    const outputPanes = panes.filter(
      (p) =>
        "inputs" in p &&
        p.inputs.findIndex(
          (paneInput) => getPaneIdFromPaneInput(paneInput) === paneToProcess.id
        ) !== -1
    );
    // TODO: handle alternate inputs like template and LLM inputSource
    outputPanes.forEach((outputPane) => {
      if (!rv.includes(outputPane)) {
        rv.push(outputPane);
        panesToProcess.push(outputPane);
      }
    });
  }
  return rv;
}

function getConnectedPanes(panes: Pane[], pane: Pane): Pane[] {
  let prevReturnValueLength = 0;
  let rv: Pane[] = [pane];
  while (rv.length !== prevReturnValueLength) {
    prevReturnValueLength = rv.length;
    rv = getUpstreamPanes(panes, rv);
    rv = getDownstreamPanes(panes, rv);
  }
  return rv;
}

const PaneHeaderContextMenu = forwardRef(
  (
    { paneId }: { paneId: string },
    forwardedRef: React.Ref<HTMLDivElement> | undefined
  ) => {
    return (
      <RadixContextMenuContent ref={forwardedRef}>
        <RadixContextMenuItem
          onClick={action(() => {
            selectedPaneIdsAtom.value = getConnectedPanes(
              canvasAtom.value.panes,
              canvasAtom.value.panes.find((p) => p.id === paneId)!
            ).map((p) => p.id);
          })}
        >
          select connected panes
        </RadixContextMenuItem>
        <RadixContextMenuItem
          onClick={action(() => {
            selectedPaneIdsAtom.value = getDownstreamPanes(
              canvasAtom.value.panes,
              canvasAtom.value.panes.filter((p) => p.id === paneId)
            ).map((p) => p.id);
          })}
        >
          select all output panes
        </RadixContextMenuItem>
        <RadixContextMenuItem
          onClick={action(() => {
            selectedPaneIdsAtom.value = getUpstreamPanes(
              canvasAtom.value.panes,
              canvasAtom.value.panes.filter((p) => p.id === paneId)
            ).map((p) => p.id);
          })}
        >
          select all input panes
        </RadixContextMenuItem>
      </RadixContextMenuContent>
    );
  }
);

// https://github.com/pmndrs/use-gesture/blob/9217f29802b8e4d87dab42e2cbd7313d2179ffea/packages/core/src/utils/events.ts#L41
function isTouch(event: UIEvent) {
  return "touches" in event;
}

function getTouchList(event: TouchEvent) {
  return event.type === "touchend" ? event.changedTouches : event.targetTouches;
}

function getValueEvent<
  EventType extends TouchEvent | PointerEvent | MouseEvent
>(event: EventType): EventType extends TouchEvent ? Touch : PointerEvent {
  return (isTouch(event) ? getTouchList(event as TouchEvent)[0] : event) as any;
}

function pointerValues(
  event: PointerEvent | TouchEvent | MouseEvent
): Position {
  // if ('spaceX' in event) return [event.spaceX, event.spaceY]
  const valueEvent = getValueEvent(event);
  return [valueEvent.clientX, valueEvent.clientY];
}

export const PaneHeaderBar = observer(
  ({
    paneId,
    paneComputed,
    heightClassName,
    shouldOutlineNameInput = false,
    isMaximized,
    children,
  }: {
    paneId: string;
    paneComputed: IComputedValue<Pane | undefined>;
    heightClassName?: string;
    shouldOutlineNameInput?: boolean;
    isMaximized: boolean;
    children: React.ReactNode;
  }) => {
    const layoutId = canvasLayoutIdAtom.value;
    const headerRootRef = useRef<HTMLDivElement | null>(null);
    const activePaneState = activePaneStates.get(paneId)!;
    const rectComputed = activePaneState.rect;
    const isFocused = computed(() => focusedPaneIdAtom.value === paneId).get();
    const { showPaneBodyOnCollapsedPaneBox } = useContext(PaneComponentContext);

    const bindDrag = useDrag(
      action<Handler<"drag">>(
        ({
          event,
          offset,
          first,
          last,
          target,
          cancel,
          canceled,
          memo = { isAltDragging: false, startPosition: undefined },
        }) => {
          if (first) {
            if (
              target instanceof Node &&
              !headerRootRef.current!.contains(target)
            ) {
              cancel();
              return;
            }
            if (
              isMaximized ||
              layoutId === MAGIC_LAYOUT_ID ||
              (target instanceof Element &&
                getParentByFilter(
                  target,
                  (element) =>
                    element.tagName === "INPUT" ||
                    element.classList.contains(PaneTransformButtonClassName)
                ) !== undefined)
            ) {
              cancel();
              return;
            }
            headerRootRef.current!.style.cursor = "grabbing";
            const rect = rectComputed.get();
            const startPosition = [rect[0], rect[1]];
            const isAltDragging = canCreatePane && event.altKey;
            if (isAltDragging) {
              assert(!(event instanceof KeyboardEvent));
              handleAltDragPane(
                canvasAtom.value.panes.filter((p) => p.id === paneId),
                pointerValues(event)
              );
            }
            showPaneBodyOnCollapsedPaneBox.set(false);
            return {
              isAltDragging,
              startPosition,
            };
          }
          const { isAltDragging } = memo;
          if (!isAltDragging && !canceled) {
            const newX = Math.floor(offset[0] / 16) * 16;
            const newY = Math.floor(offset[1] / 16) * 16;
            const rect = rectComputed.get();
            if (newX !== rect[0] || newY !== rect[1]) {
              paneMutator.updatePosition(paneId, [newX, newY]);
            }
          }
          if (last && !canceled) {
            if (!isAltDragging) {
              movePaneToTopIfNeeded(paneId);
            }
            headerRootRef.current!.style.cursor = "";
          }
          return memo;
        }
      ),
      {
        from: () => [rectComputed.get()[0], rectComputed.get()[1]],
        filterTaps: true,
        transform: ([x, y]) =>
          untracked(() =>
            roundPosition(
              convertViewportPositionToCanvasOffset(
                [x, y],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              )
            )
          ),
      }
    );

    const canCreatePane = canCreateNewPaneComputed.get();
    const viewMode = activePaneState.viewMode.get();
    useEffect(
      () =>
        autorun(() => {
          if (activePaneState.viewMode.get() === PaneViewMode.Collapsed) {
            headerRootRef.current!.style.height = `${
              activePaneState.rect.get()[3] + 1
            }px`;
            headerRootRef.current!.style.paddingBottom = "1px";
          } else {
            headerRootRef.current!.style.height = "";
            headerRootRef.current!.style.paddingBottom = "";
          }
        }),
      []
    );

    return (
      <RadixContextMenu.Root>
        <RadixContextMenu.Trigger
          asChild={true}
          // no current way to disable RadixContextMenu
          // https://github.com/radix-ui/primitives/issues/1745
          onContextMenu={(e) => {
            if (isMaximized || isOnCustomLayoutComputed.get()) {
              e.preventDefault();
            }
          }}
        >
          <div
            {...bindDrag()}
            className={classNames(
              "bg-neutral-125 dark:bg-neutral-50 hover:bg-neutral-150 border-b flex items-center shrink-0 rounded-t px-2 transition-colors group touch-none",
              !isMaximized && heightClassName !== undefined
                ? heightClassName
                : "h-8",
              viewMode === PaneViewMode.Collapsed
                ? "rounded-b border-transparent"
                : "border-neutral-150 dark:border-neutral-200",
              {
                "cursor-grab": !isMaximized && layoutId !== MAGIC_LAYOUT_ID,
              }
            )}
            onDoubleClick={action((e) => {
              if (e.target !== headerRootRef.current) {
                return;
              }
              if (viewMode === PaneViewMode.Collapsed) {
                paneMutator.setCollapsed(paneId, false);
              } else {
                const wasMaximized = uiStateMobx.maximizedPaneId === paneId;
                uiStateMobx.maximizedPaneId = wasMaximized ? undefined : paneId;
              }
            })}
            ref={headerRootRef}
          >
            {isMaximized ? (
              <button
                onClick={action(() => {
                  if (uiStateMobx.maximizedPaneId === paneId) {
                    uiStateMobx.maximizedPaneId = undefined;
                  }
                })}
                className={classNames(
                  getHeaderCircleButtonClassName(isFocused),
                  "focus-visible:bg-primary-400 hover:bg-primary-400"
                )}
              >
                <FeatherIcon icon="minus" size="xs" />
              </button>
            ) : !isViewingLayoutComputed.get() ? (
              <>
                {layoutId !== undefined ? (
                  <Tooltip content="remove pane from layout">
                    <button
                      onClick={action(() => {
                        canvasDispatch([
                          ActionType.RemovePaneFromLayout,
                          { layoutId, paneId },
                        ]);
                        focusedPaneIdAtom.value = undefined;
                      })}
                      className={classNames(
                        getHeaderCircleButtonClassName(isFocused),
                        "focus-visible:bg-primary-400 hover:bg-primary-400"
                      )}
                    >
                      <FeatherIcon icon="x" size="xs" />
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    onClick={() => {
                      if (arePanesPartOfFunctionPane([paneId])) {
                        if (
                          !confirm(
                            "Deleting this pane will break a connected function pane. Continue?"
                          )
                        ) {
                          return;
                        }
                      }
                      addUndoDeletionToStack([paneId]);
                      canvasDispatch([
                        ActionType.DeletePanes,
                        { paneIds: [paneId] },
                      ]);
                    }}
                    className={classNames(
                      getHeaderCircleButtonClassName(isFocused),
                      "focus-visible:bg-red-400 hover:bg-red-400"
                    )}
                  >
                    <FeatherIcon icon="x" size="xs" />
                  </button>
                )}
                {layoutId === undefined ? (
                  <button
                    onClick={action(() => {
                      paneMutator.setCollapsed(
                        paneId,
                        viewMode !== PaneViewMode.Collapsed
                      );
                    })}
                    className={classNames(
                      getHeaderCircleButtonClassName(isFocused),
                      "focus-visible:bg-primary-400 hover:bg-primary-400"
                    )}
                  >
                    <FeatherIcon icon="minus" size="xs" />
                  </button>
                ) : null}
              </>
            ) : null}
            <PaneNameInput
              paneId={paneId}
              paneComputed={paneComputed}
              shouldOutline={shouldOutlineNameInput}
            />
            {children}
          </div>
        </RadixContextMenu.Trigger>
        <RadixContextMenu.Portal>
          <PaneHeaderContextMenu paneId={paneId} />
        </RadixContextMenu.Portal>
      </RadixContextMenu.Root>
    );
  }
);
